function normalizeMatch(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeRoleName(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "");
}

export function getNormalizedRoleNames(user) {
  return (user?.user?.userRoles || []).map((role) => normalizeRoleName(role?.name));
}

function isSuperAdminRoleName(roleName) {
  return roleName === "SUPERUSER" || roleName.startsWith("SUPERADMIN");
}

export function getExplicitDashboardRoleScope(user) {
  const roleNames = getNormalizedRoleNames(user);

  if (roleNames.some((roleName) => isSuperAdminRoleName(roleName))) return "national";
  if (roleNames.some((roleName) => roleName.includes("RCC") || roleName.includes("NDPC"))) {
    return "regional";
  }
  if (roleNames.some((roleName) => roleName.includes("DISTRICT"))) return "district";

  return null;
}

export function isSuperuserUser(user) {
  return getNormalizedRoleNames(user).some((roleName) => isSuperAdminRoleName(roleName));
}

function getAssignedRegionNames(user) {
  return new Set(
    (user?.user?.organisationUnits || [])
      .filter((unit) => Number(unit?.level) === 2)
      .map((unit) => normalizeMatch(unit?.name)),
  );
}

function getAssignedDistrictIds(user) {
  return new Set(
    [
      ...(user?.user?.districts || []).map((district) => district?.id),
      ...(user?.user?.organisationUnits || [])
        .filter((unit) => Number(unit?.level) === 3)
        .map((unit) => unit?.id),
    ].filter(Boolean),
  );
}

function getDistrictNodeOrgUnitId(districtNode) {
  return districtNode?.orgUnitId || districtNode?.id || null;
}

function getLocationRegionSlug(location) {
  if (!location) return null;
  if (location.level === "region") return location.slug || null;
  if (location.level === "district" || location.level === "subdistrict") {
    return location.regionSlug || null;
  }
  return null;
}

function getLocationDistrictKey(location) {
  if (!location) return null;

  if (location.level === "district") {
    return location.regionSlug && location.slug
      ? `${location.regionSlug}::${location.slug}`
      : null;
  }

  if (location.level === "subdistrict") {
    return location.regionSlug && location.districtSlug
      ? `${location.regionSlug}::${location.districtSlug}`
      : null;
  }

  return null;
}

function getScopedRegionSlugs(orgTree, user) {
  const regionNames = getAssignedRegionNames(user);
  const districtIds = getAssignedDistrictIds(user);
  const scopedRegionSlugs = new Set();

  for (const region of orgTree?.children || []) {
    if (regionNames.has(normalizeMatch(region.name))) {
      scopedRegionSlugs.add(region.slug);
      continue;
    }

    if ((region.children || []).some((district) => districtIds.has(getDistrictNodeOrgUnitId(district)))) {
      scopedRegionSlugs.add(region.slug);
    }
  }

  return scopedRegionSlugs;
}

function getScopedDistrictKeys(orgTree, user) {
  const districtIds = getAssignedDistrictIds(user);
  const scopedDistrictKeys = new Set();

  if (districtIds.size === 0) {
    return scopedDistrictKeys;
  }

  for (const region of orgTree?.children || []) {
    for (const district of region.children || []) {
      if (districtIds.has(getDistrictNodeOrgUnitId(district))) {
        const districtKey = getLocationDistrictKey(district);
        if (districtKey) {
          scopedDistrictKeys.add(districtKey);
        }
      }
    }
  }

  return scopedDistrictKeys;
}

export function getUserDashboardScope(orgTree, user) {
  const explicitScope = getExplicitDashboardRoleScope(user);
  const scopedRegionCount = getScopedRegionSlugs(orgTree, user).size;
  const scopedDistrictCount = getScopedDistrictKeys(orgTree, user).size;

  if (explicitScope === "national") return "national";
  if (scopedRegionCount > 1) return "national";
  if (explicitScope === "regional") return "regional";
  if (scopedRegionCount === 1 && scopedDistrictCount > 1) return "regional";
  if (explicitScope === "district") return "district";
  if (scopedDistrictCount === 1) return "district";

  return "national";
}

export function getFirstRegionForUser(orgTree, user) {
  const scopedRegionSlugs = getScopedRegionSlugs(orgTree, user);

  if (scopedRegionSlugs.size > 0) {
    const matchedRegion = (orgTree?.children || []).find((region) => scopedRegionSlugs.has(region.slug));
    if (matchedRegion) return matchedRegion;
  }

  return (
    orgTree?.children?.find((region) => (region.children?.length || 0) > 0) ||
    orgTree?.children?.[0] ||
    null
  );
}

export function getFirstDistrictForUser(orgTree, user) {
  const scopedDistrictKeys = getScopedDistrictKeys(orgTree, user);

  if (scopedDistrictKeys.size > 0) {
    for (const region of orgTree?.children || []) {
      const matchedDistrict = (region.children || []).find((district) => {
        const districtKey = getLocationDistrictKey(district);
        return districtKey && scopedDistrictKeys.has(districtKey);
      });

      if (matchedDistrict) {
        return matchedDistrict;
      }
    }
  }

  const firstScopedRegion = getFirstRegionForUser(orgTree, user);
  if (firstScopedRegion) {
    const firstAccessibleDistrict =
      (firstScopedRegion.children || []).find((district) => district.hasAccess !== false) ||
      firstScopedRegion.children?.[0];

    if (firstAccessibleDistrict) {
      return firstAccessibleDistrict;
    }
  }

  for (const region of orgTree?.children || []) {
    const district = (region.children || []).find((item) => item.hasAccess !== false) || region.children?.[0];
    if (district) {
      return district;
    }
  }

  return null;
}

export function getDefaultDashboardLocation(orgTree, user) {
  const roleScope = getUserDashboardScope(orgTree, user);

  if (roleScope === "regional") {
    return getFirstRegionForUser(orgTree, user) || orgTree || null;
  }

  if (roleScope === "district") {
    return getFirstDistrictForUser(orgTree, user) || orgTree || null;
  }

  return orgTree || null;
}

export function isDashboardLocationAllowed(location, orgTree, user) {
  const roleScope = getUserDashboardScope(orgTree, user);

  if (roleScope === "national") {
    return true;
  }

  if (roleScope === "regional") {
    const regionSlug = getLocationRegionSlug(location);
    if (!regionSlug) return false;

    const scopedRegionSlugs = getScopedRegionSlugs(orgTree, user);
    if (scopedRegionSlugs.size === 0) {
      return location?.level !== "national";
    }

    return scopedRegionSlugs.has(regionSlug);
  }

  if (roleScope === "district") {
    const districtKey = getLocationDistrictKey(location);
    if (!districtKey) return false;

    const scopedDistrictKeys = getScopedDistrictKeys(orgTree, user);
    if (scopedDistrictKeys.size === 0) {
      return location?.level === "district" || location?.level === "subdistrict";
    }

    return scopedDistrictKeys.has(districtKey);
  }

  return true;
}

export function getDashboardVariantForLocation(location) {
  if (location?.level === "region") return "regional";
  if (location?.level === "district" || location?.level === "subdistrict") return "district";
  return "national";
}
